import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FacadeService } from 'src/app/shared/services/facade.service';
import { LiteralsState } from '../../reducers/literals.reducer';
import * as selectors from '../../selectors/literals/store.literals.selectors';

@Injectable({
  providedIn: 'root',
})
export class StoreLiteralsFacadeService extends FacadeService {
  constructor(public override store: Store<LiteralsState>) {
    super(store);
  }

  // OBSERVABLES
  storeName$ = this.storeSelect(selectors.getStoreName);
  storeCode$ = this.storeSelect(selectors.getStoreCode);
  showInactive$ = this.storeSelect(selectors.getShowInactive);
  countryCode$ = this.storeSelect(selectors.getCountryCode);
  storeDescription$ = this.storeSelect(selectors.getStoreDescription);
  isActive$ = this.storeSelect(selectors.getIsActive);
  isDeactivated$ = this.storeSelect(selectors.getIsDeactivated);
  edit$ = this.storeSelect(selectors.getEdit);
  activate$ = this.storeSelect(selectors.getActivate);
  deactivate$ = this.storeSelect(selectors.getDeactivate);
  createNewStore$ = this.storeSelect(selectors.getCreateNewStore);
  editStore$ = this.storeSelect(selectors.getEditStore);
  storecodePattern$ = this.storeSelect(selectors.getStorecodePattern);
  languages$ = this.storeSelect(selectors.getLanguages);
  storeGroups$ = this.storeSelect(selectors.getStoreGroups);
  noStoreGroups$ = this.storeSelect(selectors.getNoStoreGroups);
  posNumber$ = this.storeSelect(selectors.getPosNumber);
  posVatNumber$ = this.storeSelect(selectors.getPosVatNumber);
  posMultiplier$ = this.storeSelect(selectors.getPosMultiplier);
  noPos$ = this.storeSelect(selectors.getNoPos);
  deleteAll$ = this.storeSelect(selectors.getDeleteAll);
  attributesType$ = this.storeSelect(selectors.getAttributesType);
  attributesCode$ = this.storeSelect(selectors.getAttributesCode);
  attributesValue$ = this.storeSelect(selectors.getAttributesValue);
  upLimit$ = this.storeSelect(selectors.getUpLimit);
  downLimit$ = this.storeSelect(selectors.getDownLimit);
  noAttributes$ = this.storeSelect(selectors.getNoAttributes);
  clubs$ = this.storeSelect(selectors.getClubs);
  noClubs$ = this.storeSelect(selectors.getNoClubs);
  brands$ = this.storeSelect(selectors.getBrands);
  noBrands$ = this.storeSelect(selectors.getNoBrands);
  couponsets$ = this.storeSelect(selectors.getCouponsets);
  noCouponsets$ = this.storeSelect(selectors.getNoCouponsets);
  users$ = this.storeSelect(selectors.getUsers);
  noUsers$ = this.storeSelect(selectors.getNoUsers);
  addressDetails$ = this.storeSelect(selectors.getAddressDetails);
  addressLine$ = this.storeSelect(selectors.getAddressLine);
  town$ = this.storeSelect(selectors.getTown);
  postcode$ = this.storeSelect(selectors.getPostcode);
  country$ = this.storeSelect(selectors.getCountry);
  region$ = this.storeSelect(selectors.getRegion);
  coordinates$ = this.storeSelect(selectors.getCoordinates);
  deactivateStore$ = this.storeSelect(selectors.getDeactivateStore);
  addBrands$ = this.storeSelect(selectors.getAddBrands);
  addClubs$ = this.storeSelect(selectors.getAddClubs);
  addCouponsets$ = this.storeSelect(selectors.getAddCouponsets);
  addStoreGroups$ = this.storeSelect(selectors.getAddStoreGroups);
  addUsers$ = this.storeSelect(selectors.getAddUsers);
  selectNone$ = this.storeSelect(selectors.getSelectNone);
  selectAll$ = this.storeSelect(selectors.getSelectAll);
  none$ = this.storeSelect(selectors.getNone);
  selectedBrands$ = this.storeSelect(selectors.getSelectedBrands);
  selectedClubs$ = this.storeSelect(selectors.getSelectedClubs);
  selectedCouponsets$ = this.storeSelect(selectors.getSelectedCouponsets);
  selectedUsers$ = this.storeSelect(selectors.getSelectedUsers);
  storeUpdateSuccess$ = this.storeSelect(selectors.getStoreUpdateSuccess);
  storeCreateSuccess$ = this.storeSelect(selectors.getStoreCreateSuccess);
  searchByUsername$ = this.storeSelect(selectors.getSearchByUsername);
  searchByCouponsetCode$ = this.storeSelect(selectors.getSearchByCouponsetCode);
  searchByCode$ = this.storeSelect(selectors.getSearchByCode);

  // OBSERVABLE'S VALUES
  get storeName() {
    return this.storeSnapshot(this.storeName$);
  }

  get storeCode() {
    return this.storeSnapshot(this.storeCode$);
  }

  get countryCode() {
    return this.storeSnapshot(this.countryCode$);
  }

  get storeDescription() {
    return this.storeSnapshot(this.storeDescription$);
  }

  get posNumber() {
    return this.storeSnapshot(this.posNumber$);
  }

  get posVatNumber() {
    return this.storeSnapshot(this.posVatNumber$);
  }

  get posMultiplier() {
    return this.storeSnapshot(this.posMultiplier$);
  }

  get attributesType() {
    return this.storeSnapshot(this.attributesType$);
  }

  get attributesCode() {
    return this.storeSnapshot(this.attributesCode$);
  }

  get attributesValue() {
    return this.storeSnapshot(this.attributesValue$);
  }

  get addBrands() {
    return this.storeSnapshot(this.addBrands$);
  }

  get addClubs() {
    return this.storeSnapshot(this.addClubs$);
  }

  get addCouponsets() {
    return this.storeSnapshot(this.addCouponsets$);
  }

  get addStoreGroups() {
    return this.storeSnapshot(this.addStoreGroups$);
  }

  get addUsers() {
    return this.storeSnapshot(this.addUsers$);
  }

  get selectedBrands() {
    return this.storeSnapshot(this.selectedBrands$);
  }

  get selectedClubs() {
    return this.storeSnapshot(this.selectedClubs$);
  }

  get selectedCouponsets() {
    return this.storeSnapshot(this.selectedCouponsets$);
  }

  get selectedUsers() {
    return this.storeSnapshot(this.selectedUsers$);
  }

  get storeUpdateSuccess() {
    return this.storeSnapshot(this.storeUpdateSuccess$);
  }

  get storeCreateSuccess() {
    return this.storeSnapshot(this.storeCreateSuccess$);
  }

  get searchByUsername() {
    return this.storeSnapshot(this.searchByUsername$);
  }

  get searchByCouponsetCode() {
    return this.storeSnapshot(this.searchByCouponsetCode$);
  }

  get searchByCode() {
    return this.storeSnapshot(this.searchByCode$);
  }
}
